import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatISO, startOfMonth } from 'date-fns'

import * as medias from 'consts/medias'
import { createQueryParameters, formatDateFields } from 'services/utils'

import { usePagination, useSortData } from 'hooks'
import { invoicesRequest } from 'store/modules/users/queries/invoices/reducer'

import { useMediaQuery } from '@material-ui/core'
import { Content, Table, TablePagination, Paper } from '@/components'

import Filter from './Filter'
import InvoiceCard from './Card'
import { DownloadLinks } from './DownloadLinks'

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const DATE_FIELDS = ['nf_data_emissao.initial', 'nf_data_emissao.final']
const DEFAULT_SORT = 'nf_data_emissao'

const initialDate = formatISO(startOfMonth(new Date()))
const finalDate = formatISO(new Date())

const initialFilterValues = {
  shipment_unity: 0,
  nf_numero: '',
  nf_placa_numero: '',
  shipping_order: { ford_nome_motorista: '' },
  nf_data_emissao: {
    initial: initialDate,
    final: finalDate,
  },
}

// --------------- 𝕄𝕒𝕚𝕟 ---------------

export default function InvoicesPage() {
  const sm = useMediaQuery(medias.sm)
  const dispatch = useDispatch()

  const count = useSelector((state) => state.users.queries.invoices.count)
  const loading = useSelector((state) => state.users.queries.invoices.loading)
  const invoices = useSelector((state) => state.users.queries.invoices.invoices)
  const [selectedFilters, setSelectedFilters] = useState(initialFilterValues)

  const { currentSort, onSortChange } = useSortData({
    initialField: DEFAULT_SORT,
  })

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage, reset } =
    usePagination()

  useEffect(() => {
    const filters = { ...selectedFilters }
    const shipmentUnity = filters.shipment_unity

    delete filters.shipment_unity

    // Effect responsible for controlling data rendering
    const queryParameters = createQueryParameters({
      currentSort,
      page,
      rowsPerPage: sm ? 50 : rowsPerPage,
      filters: formatDateFields(filters, DATE_FIELDS),
      shipment_unity: shipmentUnity,
    })
    dispatch(invoicesRequest(queryParameters))
  }, [dispatch, currentSort, page, rowsPerPage, selectedFilters, sm])

  useEffect(() => {
    // Effect responsible for reset paging state
    reset()
  }, [reset, currentSort, selectedFilters])

  return (
    <Content
      loading={loading}
      title="Consultar Notas Fiscais emitidas"
      showReturn
      SideComponent={
        <Filter
          initialValues={initialFilterValues}
          onFilter={setSelectedFilters}
        />
      }
    >
      {sm ? (
        <div>
          {invoices.map((invoice, index) => (
            <InvoiceCard current={invoice} key={index} />
          ))}
        </div>
      ) : (
        <Paper className="border-none">
          <Table
            size="medium"
            data={invoices}
            columns={[
              {
                title: 'UNID',
                field: 'id_cia',
                renderCell: ({ rowData }) =>
                  rowData.billing_company?.cia_descricao_carreg,
              },
              { title: 'NOTA FISCAL', field: 'nf_numero' },
              {
                title: 'EMISSÃO',
                field: 'nf_data_emissao',
                cellFormat: 'date',
              },
              {
                title: 'CLIENTE',
                field: 'customer.emp_razao_social',
                sortable: false,
              },
              {
                title: 'CIDADE',
                field: 'customer.emp_cidade',
                sortable: false,
              },
              {
                title: 'UF',
                field: 'customer.dsc_uf',
                sortable: false,
              },
              {
                title: 'M2',
                field: 'total_quantity',
                sortable: false,
                cellFormat: 'decimal',
                align: 'right',
              },
              {
                title: 'PESO',
                field: 'nf_peso_bruto',
                cellFormat: 'decimal',
                align: 'right',
              },
              { title: 'PLACA', field: 'nf_placa_numero' },
              {
                title: 'MOTORISTA',
                field: 'shipping_order.ford_nome_motorista',
                sortable: false,
              },
              {
                title: 'REPRES',
                field: 'representative',
                sortable: false,
              },
              {
                role: 'button',
                renderCell: ({ rowData }) => (
                  <DownloadLinks invoice={rowData} />
                ),
              },
            ]}
            groupHeader={[{ start: 12, expand: 1, label: 'DOWNLOADS' }]}
            currentSort={currentSort}
            onSortChange={onSortChange}
          />
          <TablePagination
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
          />
        </Paper>
      )}
    </Content>
  )
}
